/**
 * The root route — where the shell mounts and where the layout's redirect
 * cascade runs (RO-0-05).
 *
 * `validateSearch: shellSearchSchema` is the D2/I9 mechanism, not decoration:
 * declaring the shell-wide params HERE means every descendant route inherits
 * them in its `useSearch()` type without re-declaring them, and a route that
 * tries to claim one for itself fails to compile (see `shell/search.ts`).
 *
 * A consequence worth stating: search validation is strict, so a param no
 * matched route declares is DROPPED from the parsed search rather than carried
 * silently. That is the intended discipline — an undeclared param is exactly the
 * thing that used to collide — but it means porting a page includes declaring
 * its params, and a link carrying an undeclared param loses it. Route validators
 * merge down the tree, so the root's strictness costs a child nothing.
 */

import {
  createRootRoute,
  Outlet,
  redirect,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { useApi } from "@excellent/ui/api";
import { auth } from "@excellent/ui/api/contracts/auth";
import { shell as shellApi } from "@excellent/ui/api/contracts/shell";
import { DesktopTitleBar } from "@excellent/ui/blocks/chrome/desktop-title-bar";
import { usePathname } from "@excellent/ui/router";
import { Shell } from "../shell/shell";
import { ClientObjectPeekHost } from "../shell/object-peek-host";
import { shellSearchSchema } from "../shell/search";
import { resolveShellRedirect } from "../shell/redirects";
import { resolveConfigRedirect } from "./config-redirects";
import {
  isCanonicalAgentsPagePath,
  resolveAgentsUnmountRedirect,
  resolveDeprecatedOldProductRedirect,
  resolveReviewVerdictEvidenceRedirect,
  resolveSettingsHelpDatabaseRedirect,
  resolveUnknownOldIdRedirect,
} from "./verification-legacy-redirects";
import { getShellData } from "../shell/shell-data";
import { ModeRail } from "../shell/chrome/mode-rail";
import { TopNav } from "../shell/chrome/top-nav";
import { LeftRail } from "../shell/chrome/left-rail";
import { RailChrome } from "../shell/chrome/rail-chrome";
import { ContextRail } from "../shell/chrome/context-rail";
import { DatabaseRail } from "../shell/chrome/database-rail";
import { SettingsRail } from "../shell/chrome/settings-rail";
import { DockBar } from "../shell/chrome/dock-bar";
import { BottomNav } from "../shell/chrome/bottom-nav";
import { StageHost } from "../shell/chrome/stage-host";
import { CreateMenuListener } from "../shared/chrome/create-menu";
import { AgentsProductShell } from "../shared/agents/product-shell";
import { isAgentsProductPath } from "../shared/agents/agents-views";
import {
  isBareCanvasPath,
  isDevelopmentOnlyProductPath,
  needsVerificationShellWrap,
} from "../prototypes/bare-canvas";
import { VerificationShell } from "../verification/shell";
import { EnvProvider } from "../verification/env-provider";
import { EnvIndicator } from "../verification/env-indicator";

export const rootRoute = createRootRoute({
  validateSearch: shellSearchSchema,

  /**
   * TWO redirect tables, and the ORDER BETWEEN THEM IS BEHAVIOUR (RO-3-14).
   *
   * 1. `resolveConfigRedirect` — the 26 `next.config.ts` rules (+ the one
   *    delegated stub page). Under Next these run at the ROUTING layer, before
   *    any filesystem route resolves and therefore before the root layout
   *    renders at all.
   * 2. `resolveShellRedirect` — the layout's eight-gate mode cascade (RO-0-05).
   *
   * So config comes FIRST here, matching Next. That is not bookkeeping: for
   * `/projects/<p>/development/board` the config table sends you to
   * `…/work?area=queue&sub=board` while the cascade alone would send you to
   * `…/product?area=development&sub=board` — a different MODE. Swapping these two
   * lines silently changes destinations, which is why `test-route-tree-parity`
   * asserts the composition rather than either half in isolation.
   *
   * Both are throw-based first-match-wins, same as the originals: Next threw
   * `redirect()` out of an async server component, TanStack throws a typed
   * `redirect` out of `beforeLoad`. `location.searchStr` carries its leading `?`,
   * which is the shape both the cascade's `area=favorites` test and
   * `resolveModeRedirect` expect (it is what the `x-search` header carried).
   */
  beforeLoad: ({ location }) => {
    const productionBuild = import.meta.env.PROD;
    if (productionBuild && isDevelopmentOnlyProductPath(location.pathname)) {
      throw redirect({ to: "/", replace: true });
    }

    // `/agents` has one page whitelist in direct loads and client hops. Run it
    // before legacy redirect tables so every retired page gets the same `/` hop.
    const agentsDest = resolveAgentsUnmountRedirect(location.pathname, location.searchStr);
    if (agentsDest) throw redirect({ to: agentsDest, replace: true });

    const configDest = resolveConfigRedirect(location.pathname, location.searchStr);
    if (configDest) throw redirect({ to: configDest, replace: true });

    // WP10c / #46642 — review/verdict/evidence cluster outranks the old mode
    // cascade so `/verdicts` and `?view=review` land on Decide/Findings, not
    // the retired `/agents/verification` door. Config still runs first.
    const clusterDest = resolveReviewVerdictEvidenceRedirect(location.pathname, location.searchStr);
    if (clusterDest) throw redirect({ to: clusterDest, replace: true });

    // HG10 / #46594 — after WP10c so /work/reviews still maps to Decide.
    const oldProductDest = resolveDeprecatedOldProductRedirect(
      location.pathname,
      location.searchStr,
    );
    if (oldProductDest) throw redirect({ to: oldProductDest, replace: true });

    // WP10b / #46641 — settings/help/database aliases after WP10c, before the
    // mode cascade, so /docs /db /settings/apps land on retained utilities.
    const utilityDest = resolveSettingsHelpDatabaseRedirect(location.pathname, location.searchStr);
    if (utilityDest) throw redirect({ to: utilityDest, replace: true });

    // WP10d / #46643 — unknown old-id recovery after WP10c/b, before the
    // mode cascade, so unresolvable /agents/verification/:id lands on
    // searchable not-found instead of a blank miss.
    const recoveryDest = resolveUnknownOldIdRedirect(location.pathname, location.searchStr);
    if (recoveryDest) throw redirect({ to: recoveryDest, replace: true });

    const dest = resolveShellRedirect(location.pathname, location.searchStr, getShellData());
    if (dest) throw redirect({ to: dest, replace: true });
  },

  component: RootShell,
});

/**
 * Fill the shell's chrome slots. `DesktopTitleBar` is CSS-gated (no-op in a
 * plain browser). LeftRail + ContextRail (RO-11-05 / #44590) mirror
 * app/layout.tsx: LeftRail topSlot=RailChrome (gated on left extensions),
 * ContextRail always mounted (draws right extensions). DatabaseRail is the
 * Next database layout's extension pusher — mounted as a main-column child on
 * /database so the mobile strip sits in the page body and the desktop tree
 * floats via ContextRail. DockBar + BottomNav (RO-11-04 / #44589) fill
 * keyboardNav + overlays so root dock search and mobile bottom nav exist.
 */
/**
 * BARE CANVAS ESCAPE (design prototypes only).
 *
 * `<Shell>` is the product chrome: mode rail, top nav, left/context rails, dock.
 * A design prototype whose whole point is to propose a DIFFERENT information
 * architecture cannot render inside it — the existing rails would frame, and
 * contradict, the thing being proposed. So `/styleguide/*` prototype paths
 * render as a direct child of the root route with no chrome at all.
 *
 * This is the ONLY sanctioned bypass and it is path-gated in one place
 * (`prototypes/bare-canvas.ts`) rather than by a flag a page can set, so the set
 * of chrome-less URLs stays readable in a single list.
 *
 * ── Why this is a SPLIT rather than an early return in `ProductChrome` ──
 *
 * `RootShell` is mounted once and survives every navigation, so an early return
 * placed above the chrome's own hooks would change this component's hook COUNT
 * when you navigate from a prototype path to a product path — React throws
 * "rendered more hooks than during the previous render". Keeping the branch in a
 * parent that calls exactly one hook, and the chrome's hooks in a child that is
 * mounted or unmounted as a whole, makes the two hook sets independent.
 */
function RootShell() {
  const pathname = usePathname();
  const isAgentsSection = isCanonicalAgentsPagePath(pathname);
  const isTakeoverPath =
    pathname === "/context" || pathname === "/changes" || isAgentsSection || isBareCanvasPath(pathname);
  const content = (() => {
    if (pathname === "/context" || pathname === "/changes" || isAgentsSection) {
      return (
        <VerificationShell>
          <Outlet />
        </VerificationShell>
      );
    }
    if (isTakeoverPath) {
      // #46666 / #46667: retained product pages (settings leaves, help,
      // notifications, favorites, database, directory/operations, goals)
      // render inside the verification shell. Pages that already wrap the
      // shell (`/`, `/settings`, IA routes, `/not-found`) skip this.
      if (needsVerificationShellWrap(pathname)) {
        return (
          <VerificationShell>
            <Outlet />
          </VerificationShell>
        );
      }
      return <Outlet />;
    } else {
      return <ProductChrome />;
    }
  })();
  return (
    <>
      <DesktopTitleBar />
      {content}
    </>
  );
}

/** Fill the shell's chrome slots. Unchanged from the pre-split `RootShell`. */
function ProductChrome() {
  // ModeRail pulse instruments read workspace flags from shell data
  // (RO-11-01 / ShellData.pulse). Both default OFF in SHELL_DATA_FALLBACK —
  // a rail that hard-codes them on is a defect.
  // TopNav fields (projects, reviewCount, copilot, …) come from ShellData
  // (RO-11-01 / GET /api/shell). Do not invent defaults that hide an empty vault.
  // Shell data supplies copilot flags (RO-11-01). Both default OFF in
  // SHELL_DATA_FALLBACK — hard-coding them on would surface chat when disabled.
  const shell = getShellData();
  // The environment control lives in the root chrome, so its canonical
  // provider must receive the same account identity that the verification
  // shell uses below. Without this identity the top-nav provider stays inert.
  const authState = useApi(auth.me, {});
  const shellObservation = useApi(shellApi.get, {}, { refreshInterval: 30_000 });
  const identity =
    authState.error || shellObservation.error || !authState.data
      ? null
      : {
          authenticated: authState.data.authenticated === true,
          userId: authState.data.userId ?? null,
          handle: authState.data.handle ?? null,
          workspaceId: authState.data.workspaceId ?? null,
          shellWorkspaceId: shellObservation.data?.workspaceId ?? null,
        };
  const pathname = usePathname();
  const navigate = useNavigate();
  const location = useRouterState({ select: (s) => s.location });
  const search = location.searchStr ?? "";
  const onDatabase = pathname === "/database" || pathname.startsWith("/database/");
  // Settings layout twin: Next mounted <SettingsRail> from
  // app/settings/layout.tsx. RO-10-09 deleted app/ without porting the rail —
  // every settings page still rendered, but the right-gutter sub-nav was gone.
  const onSettings = pathname === "/settings" || pathname.startsWith("/settings/");

  return (
    <EnvProvider
      key={JSON.stringify([identity?.userId, identity?.workspaceId, identity?.shellWorkspaceId])}
      pathname={pathname}
      search={search}
      workspaceId={identity?.workspaceId == null ? null : String(identity.workspaceId)}
      canonicalIdentity={identity}
      cacheNamespace={`account:${identity?.handle ?? "unknown"}:workspace:${identity?.workspaceId ?? "none"}`}
      onNavigate={(to) => void navigate({ to, resetScroll: true })}
      onNavigateWithoutEnvironment={(to) => void navigate({ to, resetScroll: true })}
    >
      <Shell
        data={shell}
        chrome={{
          // RO-11-03 / #44588 — six-mode left navigation. Absolutely positioned
          // in the md:px-36 reserve; empty slot was the "left nav missing" bug.
          modeRail: (
            <ModeRail
              pulseEnabled={shell.pulse.enabled}
              pulseDynamicEnabled={shell.pulse.dynamic}
            />
          ),
          // RO-11-02 / #44587 — nav capsule: logo · project picker · search · play · user.
          // Empty slot was the "no navigation / no logo / no project picker" bug.
          topNav: (
            <>
              <TopNav data={shell} />
              <EnvIndicator />
            </>
          ),
          // RO-11-05 / #44590 — gated left secondary rail + workspace switcher.
          leftRail: <LeftRail topSlot={<RailChrome />} />,
          // RO-11-05 / #44590 — right-margin contextual nav (settings/db/analytics).
          contextRail: <ContextRail />,
          // RO-11-04 / #44589 — root dock search + chat pill (DockBar). keyboardNav
          // matches Next's KeyboardNavProvider placement (leaf under
          // CrumbLabelProvider); overlays carry BottomNav + peek hosts.
          // #44486 — CreateMenuListener is the sole `excellent:create-*` bridge
          // (Next mounted it beside DockBar in keyboard-nav-provider). Without it,
          // dock/desktop "Create new …" commands are dead on every page that does
          // not also mount the visible <CreateMenu> button.
          keyboardNav: (
            <>
              <DockBar
                copilotEnabled={shell.copilot.enabled}
                copilotDevelop={shell.copilot.develop}
                pages={[]}
              />
              <CreateMenuListener />
            </>
          ),
          // Peek hosts: required for ?peek=<uid> dialogs (workflow-dod-surfaces /
          // agents suites). Next mounts these in app/layout.tsx; Vite fills the
          // shell.overlays slot instead (#44566). BottomNav is md:hidden.
          // StageHost — Stage overlay for Play / Chat.
          overlays: (
            <>
              <BottomNav />
              <ClientObjectPeekHost />
              <StageHost />
            </>
          ),
        }}
      >
        {/* Section layout twins: push side:right trees into ContextRail and
          render the <lg horizontal strip in the main column. */}
        {onDatabase ? <DatabaseRail /> : null}
        {onSettings ? <SettingsRail /> : null}
        {isAgentsProductPath(pathname) ? (
          <AgentsProductShell>
            <Outlet />
          </AgentsProductShell>
        ) : (
          <Outlet />
        )}
      </Shell>
    </EnvProvider>
  );
}
